/**
 * The claim wizard — everything between "yes, I want this website" and the
 * Stripe redirect, as three short screens instead of one long form.
 *
 * The page above it owns the invite, the network calls and the chrome; this
 * component owns the questions. Renders in the "Work Order" visual system
 * (fnd-hi6.1) — the primitives (`Eyebrow`, `WoButton`, `JobSheet`,
 * `LedgerRow`, `SheetTotal`) and tokens come from `./wo`, scoped under
 * `.wo-page`, which the caller (`InvitePage`'s `activate`/`activating`
 * branch) already wraps this in.
 */

import React, { useState } from 'react';
import {
  CARD_FEATURES,
  FOUNDING_PRICING_BADGE,
  PUBLIC_PLANS,
  type PlanId,
} from '../../lib/pricing';
import {
  ChoiceTile,
  type ContactPreference,
  Field,
  fieldClass,
  GoogleMark,
  PLAN_COPY,
} from './kit';
import {
  ArrowIcon,
  CheckIcon,
  Eyebrow,
  JobSheet,
  LedgerRow,
  SheetTotal,
  WoButton,
} from './wo';

export interface ClaimSubmission {
  plan: PlanId;
  contactName: string;
  email: string;
  phone: string;
  contactPreference: ContactPreference;
  password: string;
}

interface ClaimData extends ClaimSubmission {
  confirmPassword: string;
}

type ClaimStep = 0 | 1 | 2;

const CLAIM_STEPS = [
  {
    short: 'Plan',
    title: 'Choose how much help you want.',
    description:
      'Every plan keeps this exact website. What changes is how much monthly work goes into it.',
  },
  {
    short: 'Contact',
    title: 'Who should the website point at?',
    description:
      'The number here becomes the call button on every page. We also use it to reach you while the site goes live.',
  },
  {
    short: 'Account',
    title: 'Create your login.',
    description:
      'One password and you are done. Payment is the next screen — nothing is charged until you confirm it there.',
  },
] as const;

const CONTACT_PREFERENCES: {
  value: ContactPreference;
  title: string;
  body: string;
}[] = [
  { value: 'email', title: 'Email', body: 'Written updates and links.' },
  { value: 'text', title: 'Text', body: 'Short messages, quick replies.' },
  { value: 'call', title: 'Phone call', body: 'We talk it through.' },
];

export interface ClaimWizardProps {
  businessName: string;
  /** Raw invite address — may be a scraped placeholder, which we do not prefill. */
  inviteEmail?: string;
  inviteName?: string;
  businessPhone?: string;
  initialPlan?: PlanId;
  /** True when the visitor already picked a plan, so we open on the next step. */
  planPreselected?: boolean;
  busy?: boolean;
  /** Server-side failure, rendered above the actions. */
  error?: string;
  isDemo?: boolean;
  onExit: () => void;
  onSubmit: (data: ClaimSubmission) => void;
  onGooglePrototype?: () => void;
}

export default function ClaimWizard({
  businessName,
  inviteEmail = '',
  inviteName = '',
  businessPhone = '',
  initialPlan = 'professional',
  planPreselected = false,
  busy = false,
  error,
  isDemo = false,
  onExit,
  onSubmit,
  onGooglePrototype,
}: ClaimWizardProps) {
  const [claim, setClaim] = useState<ClaimData>({
    plan: initialPlan,
    contactName: inviteName,
    email: inviteEmail,
    phone: businessPhone,
    contactPreference: 'email',
    password: '',
    confirmPassword: '',
  });
  const [step, setStep] = useState<ClaimStep>(planPreselected ? 1 : 0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);

  const chosenPlan =
    PUBLIC_PLANS.find(plan => plan.id === claim.plan) ??
    PUBLIC_PLANS.find(plan => plan.id === 'professional') ??
    PUBLIC_PLANS[0];
  const stepDef = CLAIM_STEPS[step];
  const isLastStep = step === CLAIM_STEPS.length - 1;
  const reachYou =
    CONTACT_PREFERENCES.find(option => option.value === claim.contactPreference)
      ?.title ?? 'Email';

  function update<K extends keyof ClaimData>(key: K, value: ClaimData[K]) {
    setClaim(current => ({ ...current, [key]: value }));
    setFieldErrors(current => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }

  function validate(target: ClaimStep) {
    const next: Record<string, string> = {};

    if (target === 1) {
      if (!claim.contactName.trim()) next.contactName = 'Enter your name.';
      if (!claim.email.trim()) {
        next.email = 'Enter an email address.';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(claim.email.trim())) {
        next.email = 'Enter an email address you can access.';
      }
      if (!claim.phone.trim())
        next.phone = 'Enter the number the site should ring.';
    }

    if (target === 2) {
      if (claim.password.length < 8)
        next.password = 'Use at least 8 characters.';
      if (claim.password !== claim.confirmPassword)
        next.confirmPassword = 'Passwords do not match.';
    }

    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }

  function goTo(target: ClaimStep) {
    setStep(target);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function back() {
    setFieldErrors({});
    if (step === 0) {
      onExit();
      return;
    }
    goTo((step - 1) as ClaimStep);
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();

    if (!isLastStep) {
      if (!validate(step)) return;
      goTo(Math.min(step + 1, CLAIM_STEPS.length - 1) as ClaimStep);
      return;
    }
    if (!validate(1)) {
      goTo(1);
      return;
    }
    if (!validate(2)) return;

    onSubmit({
      plan: claim.plan,
      contactName: claim.contactName.trim(),
      email: claim.email.trim().toLowerCase(),
      phone: claim.phone.trim(),
      contactPreference: claim.contactPreference,
      password: claim.password,
    });
  }

  return (
    <main className="wo-wrap wo-claim">
      <aside className="wo-claim-aside">
        <button type="button" onClick={onExit} className="wo-claim-back">
          ← Back to your website
        </button>

        <h1>
          Three short steps.
          <span className="wo-dim" style={{ display: 'block' }}>
            Then the payment screen.
          </span>
        </h1>
        <p className="wo-lede">
          Your preview, your plan and the details you enter here are waiting in
          the portal the moment you finish.
        </p>

        <div className="wo-claim-desktop">
          <ol className="wo-claim-steps">
            {CLAIM_STEPS.map((item, index) => {
              const done = index < step;
              const current = index === step;
              return (
                <li
                  key={item.short}
                  className={`wo-claim-step${current ? ' wo-claim-step--current' : done ? ' wo-claim-step--done' : ''}`}
                >
                  <button
                    type="button"
                    disabled={!done || busy}
                    onClick={() => goTo(index as ClaimStep)}
                    className="wo-claim-step-btn"
                  >
                    <span className="wo-claim-step-num">
                      {done ? <CheckIcon /> : String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="wo-claim-step-label">{item.short}</span>
                  </button>
                </li>
              );
            })}
          </ol>

          <JobSheet
            title="Your order"
            reference={businessName}
            className="wo-claim-sheet"
          >
            <LedgerRow label="Plan">
              <SheetTotal
                amount={`$${chosenPlan.monthly}`}
                note={`${chosenPlan.name} · per month`}
              />
            </LedgerRow>
            {chosenPlan.anchorMonthly && (
              <LedgerRow label="Founding rate">
                <span className="wo-dim" style={{ textDecoration: 'line-through' }}>
                  ${chosenPlan.anchorMonthly}/mo
                </span>{' '}
                — {FOUNDING_PRICING_BADGE}
              </LedgerRow>
            )}
            <LedgerRow label="Setup">$0</LedgerRow>
            <LedgerRow label="Commitment">Cancel any time</LedgerRow>
          </JobSheet>
        </div>
      </aside>

      <div className="wo-claim-main">
        <div className="wo-claim-mobile">
          <div className="wo-claim-progress-row">
            <span>
              Step {step + 1} of {CLAIM_STEPS.length}
            </span>
            <span>{stepDef.short}</span>
          </div>
          <div className="wo-claim-progress-track">
            <div
              className="wo-claim-progress-fill"
              style={{ width: `${((step + 1) / CLAIM_STEPS.length) * 100}%` }}
            />
          </div>
          {step > 0 && (
            <div className="wo-claim-plan-bar">
              <span>
                <strong>{chosenPlan.name}</strong>{' '}
                <span className="wo-dim">
                  · ${chosenPlan.monthly}/mo · $0 setup
                </span>
              </span>
              <button
                type="button"
                onClick={() => goTo(0)}
                disabled={busy}
              >
                Change
              </button>
            </div>
          )}
        </div>

        <form onSubmit={submit} noValidate className="wo-claim-card">
          <Eyebrow>
            Step {step + 1} · {stepDef.short}
          </Eyebrow>
          <h2>{stepDef.title}</h2>
          <p className="wo-lede">{stepDef.description}</p>

          {step === 0 && (
            <div className="wo-plans">
              {PUBLIC_PLANS.map(plan => {
                const selected = claim.plan === plan.id;
                const card = CARD_FEATURES[plan.id];
                return (
                  <button
                    key={plan.id}
                    type="button"
                    className="wo-plan"
                    aria-pressed={selected}
                    onClick={() => update('plan', plan.id)}
                  >
                    <div className="wo-fit">
                      <span>
                        {PLAN_COPY[plan.id].fit}
                        {plan.anchorMonthly ? ` · ${FOUNDING_PRICING_BADGE}` : ''}
                      </span>
                      <span className="wo-sel">
                        <CheckIcon /> Selected
                      </span>
                    </div>
                    <div className="wo-top">
                      <span className="wo-pn">{plan.name}</span>
                      <span className="wo-pp wo-num">
                        {plan.anchorMonthly && (
                          <span className="wo-was">${plan.anchorMonthly}</span>
                        )}
                        ${plan.monthly}
                        <small>/ MO</small>
                      </span>
                    </div>
                    <p className="wo-pd">{PLAN_COPY[plan.id].label}</p>
                    {selected && (
                      <ul>
                        {card.items.map(item => (
                          <li key={item}>
                            <CheckIcon />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </button>
                );
              })}
              <p className="wo-claim-google-note">
                $0 setup · no minimum term, cancel any time · the plan can
                change later from your account.
              </p>
            </div>
          )}

          {step === 1 && (
            <div className="wo-claim-fields">
              <div className="wo-claim-grid-2">
                <Field
                  label="Your name"
                  htmlFor="claim-name"
                  error={fieldErrors.contactName}
                >
                  <input
                    id="claim-name"
                    type="text"
                    value={claim.contactName}
                    onChange={event =>
                      update('contactName', event.target.value)
                    }
                    autoComplete="name"
                    placeholder="John Smith"
                    className={fieldClass}
                  />
                </Field>
                <Field
                  label="Phone customers should call"
                  htmlFor="claim-phone"
                  error={fieldErrors.phone}
                  hint="Every call button on the site rings this number."
                >
                  <input
                    id="claim-phone"
                    type="tel"
                    value={claim.phone}
                    onChange={event => update('phone', event.target.value)}
                    autoComplete="tel"
                    placeholder="(608) 555-0134"
                    className={fieldClass}
                  />
                </Field>
              </div>

              <Field
                label="Email"
                htmlFor="claim-email"
                error={fieldErrors.email}
                hint={
                  inviteEmail
                    ? 'We filled in the email attached to the invitation. Change it if another address should own the account.'
                    : 'We did not have a reliable email for this business, so you choose the account address now.'
                }
              >
                <input
                  id="claim-email"
                  type="email"
                  value={claim.email}
                  onChange={event => update('email', event.target.value)}
                  autoComplete="email"
                  placeholder="you@business.com"
                  className={fieldClass}
                />
              </Field>

              <fieldset>
                <legend className="wo-claim-legend">
                  How should we reach you while the site goes live?
                </legend>
                <div className="wo-tiles">
                  {CONTACT_PREFERENCES.map(option => (
                    <ChoiceTile
                      key={option.value}
                      title={option.title}
                      body={option.body}
                      selected={claim.contactPreference === option.value}
                      onSelect={() => update('contactPreference', option.value)}
                    />
                  ))}
                </div>
              </fieldset>
            </div>
          )}

          {step === 2 && (
            <div className="wo-claim-fields">
              {isDemo && onGooglePrototype && (
                <div>
                  <WoButton
                    type="button"
                    tone="ghost"
                    onClick={onGooglePrototype}
                    disabled={busy}
                    style={{ width: '100%' }}
                  >
                    <GoogleMark />
                    {busy ? 'Connecting…' : 'Continue with Google'}
                    <span className="wo-pill">Prototype</span>
                  </WoButton>
                  <p className="wo-claim-google-note">
                    This shows the intended Google path. OAuth is not connected
                    yet.
                  </p>
                  <div className="wo-claim-divider">or set a password</div>
                </div>
              )}

              <Field
                label="Password"
                htmlFor="claim-password"
                error={fieldErrors.password}
              >
                <div className="wo-claim-password">
                  <input
                    id="claim-password"
                    type={showPassword ? 'text' : 'password'}
                    value={claim.password}
                    onChange={event => update('password', event.target.value)}
                    autoComplete="new-password"
                    placeholder="At least 8 characters"
                    className={fieldClass}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(value => !value)}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </Field>

              <Field
                label="Confirm password"
                htmlFor="claim-confirm-password"
                error={fieldErrors.confirmPassword}
              >
                <input
                  id="claim-confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  value={claim.confirmPassword}
                  onChange={event =>
                    update('confirmPassword', event.target.value)
                  }
                  autoComplete="new-password"
                  placeholder="Repeat the password"
                  className={fieldClass}
                />
              </Field>

              <JobSheet title="What happens when you continue">
                <LedgerRow label="Website">{businessName}</LedgerRow>
                <LedgerRow label="Plan">
                  {chosenPlan.name} · ${chosenPlan.monthly}/mo · $0 setup
                </LedgerRow>
                <LedgerRow label="Login">{claim.email || '—'}</LedgerRow>
                <LedgerRow label="Calls ring">{claim.phone || '—'}</LedgerRow>
                <LedgerRow label="We reach you by">{reachYou}</LedgerRow>
              </JobSheet>
            </div>
          )}

          {error && (
            <div role="alert" className="wo-claim-error">
              {error}
            </div>
          )}

          <div className="wo-claim-actions">
            <WoButton type="button" tone="ghost" onClick={back} disabled={busy}>
              ← {step === 0 ? 'Back to your website' : 'Back'}
            </WoButton>

            <WoButton type="submit" disabled={busy}>
              {busy
                ? 'Activating…'
                : isLastStep
                  ? isDemo
                    ? 'Finish with email'
                    : 'Activate and continue to payment'
                  : 'Continue'}
              {!busy && <ArrowIcon />}
            </WoButton>
          </div>

          <p className="wo-claim-note">
            {isDemo
              ? 'Demo mode · no account or payment will be created'
              : isLastStep
                ? `$${chosenPlan.monthly}/mo charged at checkout · $0 setup · cancel any time`
                : 'No card needed until the last step'}
          </p>
        </form>
      </div>
    </main>
  );
}
